/**
 * Agent User Management 管理器
 * 负责 Agent 用户管理模块的业务逻辑
 * @author Joey
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { agentUserActorDetail } from "@/services/agent-user-actor-detail";
import { agentUserCreate } from "@/services/agent-user-create";
import { deleteAgentUser } from "@/services/agent-user-delete";
import { toggleAgentUserStatus } from "@/services/agent-user-toggle-status";
import { canUseWindow } from "@/utils/canuseWindow";
import deepEqual from "@/utils/deep-equal";
import { actorRename } from "@/services/rename-actor-template";
import { videoUserActorTemplate } from "@/services/video-user-actor-template";
import { videoRetryFailedTemplate } from "@/services/video-retry-failed-template";
import { voiceRename } from "@/services/audio-rename-template";
import { agentUserSaveActorInfo } from "@/services/agent-user-save-actor-info";
import { checkActorNameDuplicate } from "@/services/users-check-actor-name-duplicate";
import { assetsVoiceManager } from "./assets-voice-manager";
import { useRouteNavigateHook } from "./route-navigate-manager";
import { actorDelete } from "@/services/actor-delete";
import { voiceDelete } from "@/services/audio-delete-template";
import { AgentUsersData, agentUsersList } from "@/services/agent-v2-users-list";
import { agentV2UsersDetail, SubscribeInfoData, UserAgentInfoData, UserBasicInfoData } from "@/services/agent-v2-users-detail";
import { agentV2UsersAvatarUpload } from "@/services/agent-v2-users-avatar-upload";
import { agentV2UsersUpdateAll } from "@/services/agent-v2-users-update-all";
import { agentV2UsersAssetsDetail, PushSettingData } from "@/services/agent-v2-users-assets-detail";
import { agentV2UsersSaveAssetsDetail } from "@/services/agent-v2-users-save-assets-detail";
import { agentUserGetAudioTemplates, AudioTemplatesData } from "@/services/agent-user-get-audio-templates";
import { agentUserSaveAudioTemplate, AgentUserSaveAudioTemplateRequest } from "@/services/agent-user-save-audio-templates";
import { audioRenameTemplate } from "@/services/agent-user-rename-audio-template";
import { agentUserDeleteAudioTemplate } from "@/services/agent-user-delete-audio-template";
import { agentPermissionManager } from "./agent-dashboard-permission-manager";
import { agentOrderManager } from "./agent-dashboard-order-manager";
import { agentContentManager } from "./agent-dashboard-content-manager";
import { agentDataOverviewManager } from "./agent-dashboard-data-overview-manager";
import { updateArticleHeaderDrainage } from "@/services/update-article-header-drainage";
import { agentPersonalInfoTemplateGet } from "@/services/agent-personal-info-template-get";
import { orderAttachmentUpload } from "@/services/agent-v2-order-attachment-upload";
import { agentCompanyAssetManager } from "./agent-dashboard-company-asset-manager";
import { agentOperationLogManager } from "./agent-dashboard-operation-log-manager";
import { cosUploadFile } from "@/services/cos-upload-file";

export interface AgentUserActorData extends Actor {
  createdAt: string;
  generateStatus: number;
}

interface CreateUserFormData {
  username: string;
  gender: string;
  email: string;
  company: string;
  personalTitle: string;
}

// 临时数据
interface TemplateActor {
  id: string,
  generateStatus: number,
  name: string,
  image: string,
  createdAt: string,
  audio: {
    name: string,
  }
}

export interface SearchFormData {
  searchType: string;
  inputText: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: number;
  total?: number;
}

interface SelectedActorFormData {
  actorName: string;
  actorId: string;
  voiceName: string;
  voiceId: string;
  voiceUrl: string;
}

export interface PushSettingsData {
  type: number; // 推送设置类型
  pushFrequencyDay: number;
  pushCount: number;
  pushTime: number;
}

// 智能体用户详情设置标签页
export type SettingTab = 'video' | 'podcast' | 'moments' | 'image' | 'article'

// 智能体用户详情设置标签映射
export const SETTING_TABS = {
  video: 'video',
  podcast: 'podcast',
  moments: 'moments',
  image: 'image',
  article: 'article',
} as const;

// 订阅设置映射
export const SUBSCRIPTION_MAP = {
  ai: 'aiSubscribe',
  video: 'videoSubscribe',
  podcast: 'blogSubscribe',
  moments: 'momentsSubscribe',
  image: 'graphicSubscribe',
  article: 'articleSubscribe',
} as const;

type SubscriptionType = keyof typeof SUBSCRIPTION_MAP;

// 推送设置类型
export const PUSH_SETTINGS_TYPE = {
  video: 1, // 短视频
  podcast: 2, // 播客
  moments: 3, // 朋友圈
  image: 4, // 图片
  article: 5, // 文章
} as const;

// 文章模板
export interface ArticleSettingData {
  // 页眉
  articleHeaderCopy: string,
  // 引流底图
  articleDrainageImage: string,
}

type AgentUserActorsChangeEventDetail = AgentUserActorData[]
type AgentUserActorsChangeEvent = CustomEvent<AgentUserActorsChangeEventDetail>;

type VoicesChangeEventDetail = Audio[]
type VoicesChangeEvent = CustomEvent<VoicesChangeEventDetail>;

type AgentUsersListChangeEventDetail = AgentUsersData[]
type AgentUsersListChangeEvent = CustomEvent<AgentUsersListChangeEventDetail>;


type UserCreateChangeEventDetail = CreateUserFormData
type UserCreateChangeEvent = CustomEvent<UserCreateChangeEventDetail>;

type SelectedActorChangeEventDetail = SelectedActorFormData
type SelectedActorChangeEvent = CustomEvent<SelectedActorChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

type activeSettingTabChangeEventData = { activeSettingTab: SettingTab };

type PushSettingsChangeEventDetail = PushSettingData[]
type PushSettingsChangeEvent = CustomEvent<PushSettingsChangeEventDetail>;

type UserBasicInfoChangeEventDetail = UserBasicInfoData
type UserBasicInfoChangeEvent = CustomEvent<UserBasicInfoChangeEventDetail>;

type UserAgentInfoChangeEventDetail = UserAgentInfoData
type UserAgentInfoChangeEvent = CustomEvent<UserAgentInfoChangeEventDetail>;

type SubscribeInfoChangeEventDetail = SubscribeInfoData
type SubscribeInfoChangeEvent = CustomEvent<SubscribeInfoChangeEventDetail>;

type PodcastVoicesChangeEventDetail = AudioTemplatesData[]
type PodcastVoicesChangeEvent = CustomEvent<PodcastVoicesChangeEventDetail>;

type SubscriptionVisibilityChangeEventDetail = number[]
type SubscriptionVisibilityChangeEvent = CustomEvent<SubscriptionVisibilityChangeEventDetail>;

type PushSettingVisibilityChangeEventDetail = { [key: number]: number }
type PushSettingVisibilityChangeEvent = CustomEvent<PushSettingVisibilityChangeEventDetail>;

type ArticleSettingChangeEventDetail = ArticleSettingData
type ArticleSettingChangeEvent = CustomEvent<ArticleSettingChangeEventDetail>;

export class AgentUserManagementManager {
  private static instance: AgentUserManagementManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_USER_LIST";
  private agentId: string = "";
  // 当前激活的设置标签页
  private activeSettingTab: SettingTab = "" as SettingTab;
  // 智能体用户列表数据
  private agentUsersList: AgentUsersData[] = [];

  // ========= 用户详情上半部分 ========
  // 用户基本信息数据
  private userBasicInfo: UserBasicInfoData = {} as UserBasicInfoData;
  private originUserBasicInfo: UserBasicInfoData = {} as UserBasicInfoData;

  // 用户的智能体信息
  private userAgentInfo: UserAgentInfoData = {} as UserAgentInfoData;
  private originUserAgentInfo: UserAgentInfoData = {} as UserAgentInfoData;

  // 用户订阅信息数据
  private subscribeInfo: SubscribeInfoData = {} as SubscribeInfoData;
  private originSubscribeInfo: SubscribeInfoData = {} as SubscribeInfoData;
  // ========= End ========

  // 用户数字人信息数据
  private agentUserActors: AgentUserActorData[] = [];
  // 临时数据
  private tempActors: TemplateActor[] = [];
  // 用户音频数据（来源资产的音频数据，变量名不改了）
  private agentUserVoices: Audio[] = [];
  // 用户播客音频数据（智能体用户播客的音频）
  private podcastVoices: AudioTemplatesData[] = [];

  // 轮询
  private polling: NodeJS.Timeout | null = null;
  // 视频轮询映射
  private generatorPollingMap: { [id: string]: NodeJS.Timeout } = {};

  // 推送设置数据
  private pushSettings: PushSettingData[] = [];
  private originPushSettings: PushSettingData[] = [];

  // 订阅设置可见性数据
  private subscriptionVisibility: number[] = [];

  // 推送设置可见性数据
  private pushSettingVisibility: {
    [key: number]: number;
  } = {};

  // 文章设置数据
  private articleSetting: ArticleSettingData = {
    articleHeaderCopy: '',
    articleDrainageImage: '',
  } as ArticleSettingData;
  private originArticleSetting: ArticleSettingData = {
    articleHeaderCopy: '',
    articleDrainageImage: '',
  } as ArticleSettingData;

  // 智能体用户简介模板
  private agentPersonalInfoTemplate: string = '';

  // 搜索表单的数据
  private searchForm: SearchFormData = {
    searchType: "",
    inputText: "",
    startDate: "",
    endDate: "",
    page: 1,
    pageSize: 10,
    total: 0,
  }

  // 创建用户表单数据
  private createFormData: CreateUserFormData = {
    username: "",
    gender: "",
    email: "",
    company: "",
    personalTitle: "",
  };

  // 选中的数字人表单数据
  private selectedActorFormData: SelectedActorFormData = {
    actorName: "",
    actorId: "",
    voiceName: '',
    voiceId: '',
    voiceUrl: '',
  }

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentUserManagementManager {
    if (!AgentUserManagementManager.instance) {
      AgentUserManagementManager.instance = new AgentUserManagementManager();
    }
    return AgentUserManagementManager.instance;
  }

  // 初始化
  public async init(agentId: string) {
    this.agentId = agentId;
    // 初始化时，如果用户列表为空，才请求数据
    if (this.getAgentUsersList().length === 0) {
      await this.fetchAgentUsersList();
    }
  }

  // 请求智能体用户列表数据
  public async fetchAgentUsersList(page?: number) {
    try {
      const data = await agentUsersList({
        ...this.getSearchForm(),
        agentId: this.agentId,
        page: page ?? this.searchForm.page,
      });
      this.setAgentUsersList(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent users list:", error);
      this.dispatchAgentUsersListChange();
      throw error;
    }
  }

  /** 
   * 创建用户
   * @deprecated 该接口已废弃
  */
  async createUser(formData: CreateUserFormData) {
    try {
      await agentUserCreate({
        agentId: this.agentId,
        ...formData,
      });
      // 刷新用户列表
      this.fetchAgentUsersList();
    } catch (error) {
      console.error("Failed to create user:", error);
      throw error;
    }
  }

  // 切换智能体用户启用禁用
  async toggleUserStatus(userId: string) {
    try {
      await toggleAgentUserStatus({
        agentId: this.agentId,
        userId,
        roleType: 4,
      });
    } catch (error) {
      console.error("Failed to toggle user status:", error);
      throw error;
    }
  }

  // 获取用户详情
  async fetchAgentV2UsersDetail(userId: string) {
    try {
      const data = await agentV2UsersDetail({
        userId,
        agentId: this.getAgentId(),
      });

      // 如果没有数据，要抛出错误，在hook中要返回到上一页
      if (!data) throw new Error("User is not found");

      // 用户基础信息
      this.setUserBasicInfo(data.userBasicInfo);
      this.setOriginUserBasicInfo(data.userBasicInfo);

      // 智能体用户信息
      this.setUserAgentInfo(data.userAgentInfo);
      this.setOriginUserAgentInfo(data.userAgentInfo);

      // 订阅设置数据
      this.setSubscribeInfo(data.subscribeInfo);
      this.setOriginSubscribeInfo(data.subscribeInfo);

      // 文章设置数据
      this.setArticleSetting(data.articleFormatSettings);
      this.setOriginArticleSetting(data.articleFormatSettings);

      // 推送设置可见性数据
      this.setPushSettingVisibility(data.pushSettings);
    } catch (error) {
      console.error("Failed to fetch user detail:", error);
      throw error;
    }
  }

  // 获取用户推送设置
  async fetchPushSettings({ userId, agentId }: { userId: string, agentId?: string }) {
    try {
      const data = await agentV2UsersAssetsDetail({
        userId,
        agentId: agentId || this.getAgentId(),
      });

      this.setOriginPushSettings(data.pushSetting);
      this.setPushSettings(data.pushSetting);

      this.setSubscriptionVisibility(data.pushSettingVisibility);

      // 如果有订阅设置，则默认选中第一个订阅设置
      if (data.pushSettingVisibility.length > 0) {
        const firstTab = Math.min(...data.pushSettingVisibility)
        for (const [key, value] of Object.entries(PUSH_SETTINGS_TYPE)) {
          if (value === firstTab) {
            this.setActiveSettingTab(key as SettingTab);
            break;
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch push settings:", error);
      throw error;
    }
  }

  // 更新用户信息
  async updateUser() {
    try {
      // 基础信息、智能体信息、订阅信息
      const params = {
        agentId: this.getAgentId(),
        userId: this.getUserBasicInfo().userId,
        basicUserName: this.getUserBasicInfo().name,
        gender: this.getUserBasicInfo().gender,
        roleStatus: this.getUserBasicInfo().roleStatus,

        avatar: this.getUserAgentInfo().avatar,
        agentUserName: this.getUserAgentInfo().name,
        personalTitle: this.getUserAgentInfo().personalTitle,
        introduction: this.getUserAgentInfo().introduction,

        aiSubscribe: this.getSubscribeInfo().aiSubscribe,
        videoSubscribe: this.getSubscribeInfo().videoSubscribe,
        articleSubscribe: this.getSubscribeInfo().articleSubscribe,
        blogSubscribe: this.getSubscribeInfo().blogSubscribe,
        momentsSubscribe: this.getSubscribeInfo().momentsSubscribe,
        graphicSubscribe: this.getSubscribeInfo().graphicSubscribe,
        chatSubscribe: this.getSubscribeInfo().chatSubscribe,
      }
      await agentV2UsersUpdateAll(params);
    } catch (error) {
      console.error("Failed to update user:", error);
      throw error;
    }
  }

  // 更新用户推送设置
  async updatePushSettings(userId: string) {
    try {
      // 过滤掉订阅设置中关闭的对应pushSettings的type的数据
      const filteredPushSettings = this.getPushSettings().filter(pushSetting => {
        // 取出推送设置的type对应的键名
        let subscribeKey: SubscriptionType | undefined;
        for (const [key, value] of Object.entries(PUSH_SETTINGS_TYPE)) {
          if (value === pushSetting.type) {
            subscribeKey = key as SubscriptionType;
            break;
          }
        }
        // 根据订阅设置键名获取订阅设置值
        if (subscribeKey) {
          const subscribeSetting = this.getSubscribeInfo()[SUBSCRIPTION_MAP[subscribeKey]];
          // 如果订阅设置为1（开启），则保留该项
          return subscribeSetting === 1;
        }
        return true;
      });
      const params = {
        agentId: this.getAgentId(),
        userId: userId,
        pushSetting: filteredPushSettings,
      }
      await agentV2UsersSaveAssetsDetail(params);
    } catch (error) {
      console.error("Failed to update push settings:", error);
      throw error;
    }
  }

  // 上传用户头像
  public async uploadUserAvatar(file: File) {
    try {
      const res = await cosUploadFile(file, "common");
      this.setUserAgentInfo({
        ...this.getUserAgentInfo(),
        avatar: res.url,
      })
    } catch (error) {
      console.error("Failed to upload user avatar:", error);
      throw error;
    }
  }

  // 删除用户
  async deleteUser(userId: string) {
    try {
      await deleteAgentUser({
        agentId: this.agentId,
        userId,
        roleType: 4,
      });
    } catch (error) {
      console.error("Failed to delete user:", error);
      throw error;
    }
  }

  // 获取用户数字人/音频数据
  async fetchAgentUserActors(inputText?: string) {
    const _userId = new URLSearchParams(location.search).get('userId');
    if (!_userId) return;
    try {
      const data = await agentUserActorDetail({
        userId: _userId,
        inputText: inputText || '',
        agentId: this.getAgentId()
      });

      // 处理音频数据
      const voices = data.audios?.map((item) => ({
        id: item.id,
        voiceTtsId: item.voiceTtsId,
        name: item.name,
        modelName: item.provider,
        ageGroup: "",
        language: "",
        gender: "",
        emotion: "",
        speed: 1,
        isStarred: false,
        audioUrl: item.sampleUrl,
        createdAt: item.createdAt,
      }));
      this.setAgentUserVoices(voices);

      // 如果进行搜索音频操作，不需要重新设置数字人数据
      if (inputText) return;

      // 处理数字人数据
      const actors = data.actors?.map(item => ({
        id: item.id,
        modelId: item.modelId,
        name: item.name,
        video: item.fullActorDefaultVideoUrl,
        tags: item.tags,
        image: item.imageUrl,
        isFavorite: item.isFavorite,
        isMyActor: item.isMyActor,
        createdAt: item.createdAt,
        isPro: item.isPro,
        isHD: item.isHD,
        generateStatus: item.generateStatus,
        generateType: item.generateType,
        width: item.width,
        height: item.height,
        audio: {
          id: item.defaultAudio?.id,
          voiceTtsId: item.defaultAudio?.voiceTtsId,
          modelName: item.defaultAudio?.modelName,
          name: item.defaultAudio?.name,
          speed: item.defaultAudio?.speed,
          audioUrl: item.defaultAudio?.audioUrl,
          isStarred: item.defaultAudio?.isStarred,
          language: '',
          gender: '',
          ageGroup: '',
        },
        isUnusual: item.isUnusual,
      }));

      // 清除临时结果
      this.clearTempActors();
      this.setAgentUserActors(actors);

      // 检查是否有生成中的视频 如果有需要进行单独的任务轮询
      // for (const actor of actors) {
      //   // 状态是生成中且 id 有值才需要轮询
      //   if (actor.generateStatus < 2 && actor.id) {
      //     this.generatorPolling(actor.id);
      //   }
      // }

    } catch (error) {
      this.dispatchAgentUserActorsChange();
      console.error("Failed to fetch agent user actors:", error);
      throw error;
    }
  }

  // 请求播客音频数据
  async fetchPodcastVoices({ agentId, userId }: { agentId?: string, userId?: string }) {
    try {
      const data = await agentUserGetAudioTemplates({
        agentId: agentId || this.agentId,
        userId: userId,
        page: 1,
        pageSize: 1000,
      });
      this.setPodcastVoices(data.audios);
    } catch (error) {
      this.dispatchPodcastVoicesChange();
      console.error("Failed to fetch podcast voices:", error);
      throw error;
    }
  }

  // 保存播客音频
  async savePodcastVoices(templates: AgentUserSaveAudioTemplateRequest[]) {
    try {
      await agentUserSaveAudioTemplate(templates);
    } catch (error) {
      console.error("Failed to save podcast voices:", error);
      throw error;
    }
  }

  // 删除播客音频
  public async deletePodcastVoice({ id, userId }: { id: string, userId: string }) {
    // 乐观更新：立即从本地状态删除
    const voices = [...this.getPodcastVoices()];
    const newVoices = this.getPodcastVoices().filter((item) => id !== item.id);
    this.setPodcastVoices(newVoices);
    this.dispatchPodcastVoicesChange();
    try {
      // 调用 API 删除数据
      await agentUserDeleteAudioTemplate({
        userId,
        audioId: id,
        agentId: this.getAgentId(),
      });
    } catch (error) {
      // 失败，回滚到原始状态
      this.setPodcastVoices(voices);
      this.dispatchPodcastVoicesChange();
      throw error;
    }
  }

  // 播客音频重命名
  public async renamePodcastVoice({
    voiceId,
    name,
    userId
  }: {
    voiceId: string,
    name: string,
    userId?: string
  }) {
    // 用于回退
    const originVoices = [...this.podcastVoices];
    try {
      // 乐观更新
      const voices = this.podcastVoices.map((item) => {
        if (item.id === voiceId) {
          return {
            ...item,
            name,
          };
        }
        return item;
      });
      this.setPodcastVoices(voices);
      const res = await audioRenameTemplate({ id: voiceId, name, userId });
      if (res.isDuplicate) {
        // 回退更新
        this.setPodcastVoices(originVoices);
      }
      // 继续返回结果
      return res;
    } catch (error) {
      // 回退更新
      this.setPodcastVoices(originVoices);
      // 继续抛出异常
      throw error;
    }
  }

  // 重新生成数字人
  public async regenerateActor(actor: Actor, userId: string) {
    const _actors = [...this.agentUserActors];
    try {
      const temp = {
        id: actor.id,
        generateStatus: 1,
        name: actor?.name,
        image: actor.image,
        createdAt: actor.createdAt,
        audio: {
          name: actor?.audio?.name,
        }
      }
      this.setTempActors(temp);
      // 删除当前点击的数据
      const filteredActors = this.agentUserActors.filter(item => item.id !== actor.id);
      this.setAgentUserActors(filteredActors);

      // 发起重新生成请求
      await videoRetryFailedTemplate(actor.id);
      this.fetchAgentUserActors();
    } catch (error) {
      // 回滚
      this.setAgentUserActors(_actors);
      this.setTempActors(this.tempActors.filter(item => item.id !== actor.id));
      console.error("Failed to regenerate actor:", error);
      throw error
    }
  }

  // 保存编辑数字人
  public async saveSelectedActor() {
    try {
      const formData = this.getSelectedActorFormData();
      // 在源数据中查找当前选中的数字人
      const current = this.agentUserActors.find(item => item.id === formData.actorId);
      // 构建请求参数
      const requestData: Omit<SelectedActorFormData, 'voiceId' | 'voiceUrl'> & {
        voiceId?: string,
        voiceUrl?: string,
      } = {
        actorName: formData.actorName,
        actorId: formData.actorId,
        voiceName: formData.voiceName,
      };
      // 如果voiceId有变化，添加voiceId和voiceUrl到请求参数中
      if (current && formData.voiceId !== current.audio?.id) {
        requestData.voiceId = formData.voiceId;
        requestData.voiceUrl = formData.voiceUrl;
      }
      // 发起保存请求
      await agentUserSaveActorInfo(requestData);
      // 刷新数字人列表
      await this.fetchAgentUserActors();
    } catch (error) {
      console.error("Failed to save selected actor:", error);
      throw error
    }
  }

  // 演员删除
  public async actorDelete({ id }: { id: string }) {
    // 乐观更新：立即从本地状态删除
    const actors = [...this.agentUserActors];
    const newActors = this.agentUserActors.filter((item) => id !== item.id);
    // 数据始终以后端返回为准
    this.setAgentUserActors(newActors);
    this.dispatchAgentUserActorsChange();
    try {
      // 调用 API 删除数据
      await actorDelete(id);
      // 刷新数据，后期可改为插入返回的数据 避免重新刷新列表。
      // await this.fetchAgentUserActors();
    } catch (error) {
      // 失败，回滚到原始状态
      this.setAgentUserActors(actors);
      this.dispatchAgentUserActorsChange();
      throw error;
    }
  }

  // 演员重命名
  public async actorRename({ id, name, userId }: { id: string, name: string, userId?: string }) {
    const actors = this.getAgentUserActors();
    const actorIndex = actors.findIndex(actor => actor.id === id);
    const originalName = actors[actorIndex].name;
    const originActors = actors; // 副本
    try {
      // 乐观更新：立即更新本地状态
      const updatedActors = [...actors];
      updatedActors[actorIndex] = {
        ...updatedActors[actorIndex],
        name
      };
      this.setAgentUserActors(updatedActors);
      // 调用 API 更新数据
      const res = await actorRename({ id, name, userId });
      if (res.isDuplicate) {
        throw new Error("Actor name already exists");
      }
      return res;
    } catch (error) {
      // 失败，回滚到原始状态
      const rolledBackActors = [...originActors];
      rolledBackActors[actorIndex] = {
        ...rolledBackActors[actorIndex],
        name: originalName
      };
      this.setAgentUserActors(rolledBackActors);
      throw error;
    }
  }

  // 检查演员名称是否重复
  public async checkActorNameDuplicate(userId?: string) {
    const res = await checkActorNameDuplicate({
      name: this.getSelectedActorFormData().actorName,
      userId
    });
    return res;
  }

  // 音频重命名
  public async voiceRename({ voiceId, name, userId }: { voiceId: string, name: string, userId?: string }) {
    // 用于回退
    const originVoices = [...this.agentUserVoices];
    try {
      // 乐观更新
      const voices = this.agentUserVoices.map((item) => {
        if (item.id === voiceId) {
          return {
            ...item,
            name,
          };
        }
        return item;
      });
      this.setAgentUserVoices(voices);
      const res = await voiceRename({ id: voiceId, name, userId });
      if (res.isDuplicate) {
        // 回退更新
        this.setAgentUserVoices(originVoices);
      }
      // 继续返回结果
      return res;
    } catch (error) {
      // 回退更新
      this.setAgentUserVoices(originVoices);
      // 继续抛出异常
      throw error;
    }
  }

  // 音频删除
  public async voiceDelete({ id, userId }: { id: string, userId: string }) {
    // 乐观更新：立即从本地状态删除
    const voices = [...this.agentUserVoices];
    const newVoices = this.agentUserVoices.filter((item) => id !== item.id);
    // 数据始终以后端返回为准
    this.setAgentUserVoices(newVoices);
    this.dispatchAgentUserVoicesChange();
    try {
      // 调用 API 删除数据
      const res = await voiceDelete(id);
      if (res.isUsed) {
        // 该音频被使用，回滚数据
        this.setAgentUserVoices(voices);
      }
      return res;
    } catch (error) {
      // 失败，回滚到原始状态
      this.setAgentUserVoices(voices);
      this.dispatchAgentUserVoicesChange();
      throw error;
    }
  }

  // 添加音频
  public addVoice(voice: Audio): void {
    this.setAgentUserVoices([voice, ...this.agentUserVoices]);
  }

  // 保存文章设置
  async saveArticleSetting({ agentId, userId }: { agentId?: string, userId?: string }) {
    try {
      await updateArticleHeaderDrainage({
        agentId: agentId || this.getAgentId(),
        userId: userId,
        ...this.getArticleSetting(),
      });
    } catch (error) {
      this.dispatchArticleSettingChange();
      console.error("Failed to save article setting:", error);
      throw error;
    }
  }

  // 上传引流底图
  public async uploadArticleSettingImage(file: File) {
    try {
      // 获取附件链接
      const res = await cosUploadFile(file, "common");
      this.setArticleSetting({
        ...this.getArticleSetting(),
        articleDrainageImage: res.url,
      })
      this.dispatchArticleSettingChange();
    } catch (error) {
      console.error("Failed to upload article setting image:", error);
      throw error;
    }
  }

  // 获取智能体简介模板
  public async fetchAgentIntroductionTemplate(agentId: string) {
    try {
      const res = await agentPersonalInfoTemplateGet(agentId)
      this.setAgentPersonalInfoTemplate(res.personalInformationTemplate)
    } catch (error) {
      console.error("Failed to fetch agent introduction template:", error);
      throw error;
    }
  }


  // ============== 轮询获取生成结果 ==================

  // 单独轮询 用于获取生成视频结果
  public generatorPolling(id: string): () => void {
    // 已在轮询则清除原轮询函数
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
    }
    this.generatorPollingMap[id] = setTimeout(async () => {
      try {
        const newResult = await videoUserActorTemplate(id);
        // 同步结果
        const existingResults = this.agentUserActors || [];
        const resultIndex = existingResults.findIndex(
          (result) => result.id === newResult.id
        );
        if (resultIndex !== -1) {
          // 更新结果列表
          const updatedResults = [...existingResults];
          updatedResults[resultIndex] = {
            ...updatedResults[resultIndex],
            ...newResult,
            image: newResult.imageUrl,
            // 测试用，后面要去掉
            // name: formatDate(new Date(), 'ss') + '--' + newResult.name
          };
          // 更新数据
          this.setAgentUserActors(updatedResults);
          // 是否需要继续轮询
          newResult.generateStatus < 2
            ? this.generatorPolling(id)
            : this.stopGeneratorPolling(id);

          return;
        }
      } catch {
        // 发生错误仍需继续轮询
        this.generatorPolling(id);
        return;
      }
      // 在列表中找不到相应的数据则停止不必要的轮询
      this.stopGeneratorPolling(id);
    }, 10000);
    return () => this.stopGeneratorPolling(id);
  }

  // 停止某个单独轮询
  public stopGeneratorPolling(id: string) {
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
      delete this.generatorPollingMap[id];
    }
  }

  public stopPolling(): void {
    if (this.polling) {
      clearTimeout(this.polling);
      this.polling = null;
    }
  }

  // 停止所有轮询
  public stopAllPolling() {
    console.log('停止所有轮询')
    this.stopPolling();
    for (const id of Object.keys(this.generatorPollingMap)) {
      this.stopGeneratorPolling(id);
    }
  }

  // 重置用户列表和搜索表单
  public resetAgentUsersListAndSearchForm() {
    this.setAgentUsersList([]);
    this.setSearchForm({
      searchType: '',
      inputText: '',
      startDate: '',
      endDate: '',
      page: 1,
      pageSize: 10,
    });
  }

  // ============== 临时数据 ==================
  private setTempActors(results: TemplateActor | TemplateActor[]) {
    if (Array.isArray(results)) {
      this.tempActors = [...results]
    } else {
      this.tempActors = [results, ...this.tempActors];
    }
    this.dispatchAgentUserActorsChange();
  }

  // 清空临时结果
  private clearTempActors() {
    this.tempActors = [];
  }

  // ==================== 读取写入操作 =================
  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  public getAgentUsersList() {
    return this.agentUsersList;
  }
  public setAgentUsersList(users: AgentUsersData[]) {
    this.agentUsersList = users;
    this.dispatchAgentUsersListChange();
  }

  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(formData: SearchFormData) {
    this.searchForm = formData;
    this.dispatchSearchFormChange();
  }

  public getCreateFormData() {
    return this.createFormData;
  }
  public setCreateFormData(formData: CreateUserFormData) {
    this.createFormData = formData;
    this.dispatchCreateFormDataChange();
  }

  public getUserBasicInfo() {
    return this.userBasicInfo;
  }
  public setUserBasicInfo(userBasicInfo: UserBasicInfoData) {
    this.userBasicInfo = userBasicInfo;
    this.dispatchUserBasicInfoChange();
  }
  public getOriginUserBasicInfo() {
    return this.originUserBasicInfo;
  }
  public setOriginUserBasicInfo(userBasicInfo: UserBasicInfoData) {
    this.originUserBasicInfo = userBasicInfo;
  }

  public getUserAgentInfo() {
    return this.userAgentInfo;
  }
  public setUserAgentInfo(userAgentInfo: UserAgentInfoData) {
    this.userAgentInfo = userAgentInfo;
    this.dispatchUserAgentInfoChange();
  }
  public getOriginUserAgentInfo() {
    return this.originUserAgentInfo;
  }
  public setOriginUserAgentInfo(userAgentInfo: UserAgentInfoData) {
    this.originUserAgentInfo = userAgentInfo;
  }

  public getSubscribeInfo() {
    return this.subscribeInfo;
  }
  public setSubscribeInfo(subscribeInfo: SubscribeInfoData) {
    this.subscribeInfo = subscribeInfo;
    this.dispatchSubscribeInfoChange();
  }
  public getOriginSubscribeInfo() {
    return this.originSubscribeInfo;
  }
  public setOriginSubscribeInfo(subscribeInfo: SubscribeInfoData) {
    this.originSubscribeInfo = subscribeInfo;
  }

  public getAgentUserActors() {
    // return this.agentUserActors;
    return [...(this.tempActors as AgentUserActorData[]), ...this.agentUserActors]
  }
  public setAgentUserActors(actors: AgentUserActorData[]) {
    this.agentUserActors = actors;
    this.dispatchAgentUserActorsChange();
  }

  public getAgentUserVoices() {
    return this.agentUserVoices;
  }
  public setAgentUserVoices(voices: Audio[]) {
    this.agentUserVoices = voices;
    this.dispatchAgentUserVoicesChange();
  }

  public getSelectedActorFormData() {
    return this.selectedActorFormData;
  }
  public setSelectedActorFormData(formData: SelectedActorFormData) {
    this.selectedActorFormData = formData;
    this.dispatchSelectedActorFormDataChange();
  }

  public getActiveSettingTab() {
    return this.activeSettingTab;
  }
  public setActiveSettingTab(activeSettingTab: SettingTab) {
    this.activeSettingTab = activeSettingTab;
    this.dispatchActiveSettingTabChange();
  }

  public getPushSettings() {
    return this.pushSettings;
  }
  public setPushSettings(pushSettings: PushSettingData[]) {
    this.pushSettings = pushSettings;
    this.dispatchPushSettingsChange();
  }

  public getOriginPushSettings() {
    return this.originPushSettings;
  }
  public setOriginPushSettings(pushSettings: PushSettingData[]) {
    this.originPushSettings = pushSettings;
  }

  public getPodcastVoices() {
    return this.podcastVoices;
  }
  public setPodcastVoices(voices: AudioTemplatesData[]) {
    this.podcastVoices = voices;
    this.dispatchPodcastVoicesChange();
  }

  // 订阅设置可见性数据
  public getSubscriptionVisibility() {
    return this.subscriptionVisibility;
  }
  public setSubscriptionVisibility(visibility: number[]) {
    this.subscriptionVisibility = visibility;
    this.dispatchSubscriptionVisibilityChange();
  }

  // 推送设置可见性数据
  public getPushSettingVisibility() {
    return this.pushSettingVisibility;
  }
  public setPushSettingVisibility(visibility: { [key: number]: number }) {
    this.pushSettingVisibility = visibility;
    this.dispatchPushSettingVisibilityChange();
  }

  // 文章设置数据
  public getArticleSetting() {
    return this.articleSetting;
  }
  public setArticleSetting(setting: ArticleSettingData) {
    this.articleSetting = setting;
    this.dispatchArticleSettingChange();
  }

  public getOriginArticleSetting() {
    return this.originArticleSetting;
  }
  public setOriginArticleSetting(setting: ArticleSettingData) {
    this.originArticleSetting = setting;
  }

  public getAgentPersonalInfoTemplate() {
    return this.agentPersonalInfoTemplate;
  }
  public setAgentPersonalInfoTemplate(template: string) {
    this.agentPersonalInfoTemplate = template;
  }

  // ==================== 订阅通知操作 ====================
  // 监听智能体用户列表变化
  public onAgentUsersListChange(listener: (e: AgentUsersListChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:agent-users-list-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);;
    }
    return () => { };
  }
  public dispatchAgentUsersListChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentUsersListChangeEventDetail>(
        `${this.STORAGE_KEY}:agent-users-list-change`, {
        detail: this.agentUsersList,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听搜索表单变化
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:search-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听用户创建表单数据变化
  public onCreateFormDataChange(listener: (e: UserCreateChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:create-data-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchCreateFormDataChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<UserCreateChangeEventDetail>(
        `${this.STORAGE_KEY}:create-data-change`, {
        detail: this.createFormData,
      }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听用户基本信息
  public onUserBasicInfoChange(listener: (e: UserBasicInfoChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:basic-info-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchUserBasicInfoChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<UserBasicInfoChangeEventDetail>(
        `${this.STORAGE_KEY}:basic-info-change`, {
        detail: this.userBasicInfo,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户智能体信息
  public onUserAgentInfoChange(listener: (e: UserAgentInfoChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:agent-info-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchUserAgentInfoChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<UserAgentInfoChangeEventDetail>(
        `${this.STORAGE_KEY}:agent-info-change`, {
        detail: this.userAgentInfo,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户订阅信息变化
  public onSubscribeInfoChange(listener: (e: SubscribeInfoChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:subscribe-info-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchSubscribeInfoChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<SubscribeInfoChangeEventDetail>(
        `${this.STORAGE_KEY}:subscribe-info-change`, {
        detail: this.subscribeInfo,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听激活设置标签页变化事件
  private dispatchActiveSettingTabChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<activeSettingTabChangeEventData>(this.STORAGE_KEY, {
      detail: { activeSettingTab: this.activeSettingTab }
    });
    window.dispatchEvent(event);
  }
  public onActiveSettingTabChange(listener: (e: CustomEvent<activeSettingTabChangeEventData>) => void) {
    if (!canUseWindow()) return;
    window.addEventListener(this.STORAGE_KEY, listener);
    return () => window.removeEventListener(this.STORAGE_KEY, listener);
  }

  // 监听用户数字人数据变化
  public onAgentUserActorsChange(listener: (e: AgentUserActorsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:actors-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentUserActorsChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentUserActorsChangeEventDetail>(
        `${this.STORAGE_KEY}:actors-change`, {
        detail: this.agentUserActors,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户音频数据变化
  public onAgentUserVoicesChange(listener: (e: VoicesChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:voices-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentUserVoicesChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<VoicesChangeEventDetail>(
        `${this.STORAGE_KEY}:voices-change`, {
        detail: this.agentUserVoices,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听选中数字人表单数据变化
  public onSelectedActorFormDataChange(listener: (e: SelectedActorChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:selected-actor-data-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchSelectedActorFormDataChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<SelectedActorChangeEventDetail>(
        `${this.STORAGE_KEY}:selected-actor-data-change`, {
        detail: this.selectedActorFormData,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户推送设置数据变化
  public onPushSettingsChange(listener: (e: PushSettingsChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:push-settings-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchPushSettingsChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<PushSettingsChangeEventDetail>(
        `${this.STORAGE_KEY}:push-settings-change`, {
        detail: this.pushSettings,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户播客音频数据变化
  public onPodcastVoicesChange(listener: (e: PodcastVoicesChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:podcast-voices-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchPodcastVoicesChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<PodcastVoicesChangeEventDetail>(
        `${this.STORAGE_KEY}:podcast-voices-change`, {
        detail: this.podcastVoices,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户订阅设置可见性数据变化
  public onSubscriptionVisibilityChange(listener: (e: SubscriptionVisibilityChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:subscription-visibility-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchSubscriptionVisibilityChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<SubscriptionVisibilityChangeEventDetail>(
        `${this.STORAGE_KEY}:subscription-visibility-change`, {
        detail: this.subscriptionVisibility,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户文章设置数据变化
  public onArticleSettingChange(listener: (e: ArticleSettingChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:article-setting-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchArticleSettingChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<ArticleSettingChangeEventDetail>(
        `${this.STORAGE_KEY}:article-setting-change`, {
        detail: this.articleSetting,
      });
      window.dispatchEvent(event);
    }
  }

  // 监听用户推送设置可见性数据变化
  public onPushSettingVisibilityChange(listener: (e: PushSettingVisibilityChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:push-setting-visibility-change`;
      window.addEventListener(eventName, listener as EventListener);
      return () => window.removeEventListener(eventName, listener as EventListener);
    }
    return () => { };
  }
  public dispatchPushSettingVisibilityChange() {
    if (canUseWindow()) {
      const event = new CustomEvent<PushSettingVisibilityChangeEventDetail>(
        `${this.STORAGE_KEY}:push-setting-visibility-change`, {
        detail: this.pushSettingVisibility,
      });
      window.dispatchEvent(event);
    }
  }
}

// ========================= Hooks ==========================
// =========== 用户列表相关的 hook ==============
// 智能体列表数据
const useAgentUserListData = () => {
  const { id } = useParams();
  const toast = useToast();

  const [agentUsersList, setAgentUsersList] = useState(userManagementManager.getAgentUsersList());
  const [searchFormData, setSearchFormData] = useState(userManagementManager.getSearchForm());
  const [loading, setLoading] = useState(false);

  useListenerAfterAction('management');

  const init = async () => {
    if (!id) {
      toast.toast({
        description: `Agent id is required`,
      });
      return;
    }
    try {
      setLoading(true);
      await userManagementManager.init(id);
      setLoading(false);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await userManagementManager.fetchAgentUsersList(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number | string[]) => {
    if (key === 'startDate' || key === 'endDate') {
      userManagementManager.setSearchForm({
        ...searchFormData,
        startDate: value[0],
        endDate: value[1],
      });
      return;
    }
    userManagementManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    userManagementManager.setSearchForm({
      searchType: null,
      inputText: "",
      startDate: "",
      endDate: "",
      page: 1,
      pageSize: 10,
    });
    await search();
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = userManagementManager.onAgentUsersListChange((e) => {
      setAgentUsersList(e.detail);
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = userManagementManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail);
    })
    return unsubscribe
  }, [])

  return {
    loading,
    agentUsersList,
    searchFormData,

    search,
    changeSearchForm,
    resetAndSearch,
    changePage,
  }
}

/**
 * 监听用户管理路由变化
 * @description 当用户管理路由,跳转至非 agent/dashboard 页则清空用户列表, 工作流/订阅设置
 * @description 该逻辑也可以考虑用location的 state,search进行判读,但要注意刷新后的判断
 * @param page 
 */
const useListenerAfterAction = (page: string) => {
  const { afterListener } = useRouteNavigateHook.useRouterListener();
  const listenerPath = [
    '/agent/dashboard/user-management/',
    '/agent/dashboard/permission/',
    '/agent/dashboard/data-overview/',
    '/agent/dashboard/content-management/',
    '/agent/dashboard/order-management/',
    '/agent/dashboard/company-asset/',
    '/agent/dashboard/operation-log/',
  ];

  // 注册路由变化后置回调
  useEffect(() => {
    const id = `agent-user-${page}-after`;
    const { removeAfterListener } = afterListener(id, async (from, to) => {
      console.log(`${page} afterCallback had been triggered:`, { from, to });
      if (!listenerPath.some((path) => to.pathname.includes(path))) {
        console.log('Clear list data');
        userManagementManager.resetAgentUsersListAndSearchForm();
        agentPermissionManager.resetPermissionListAndSearchForm();
        agentOrderManager.resetOrderListAndSearchForm();
        agentContentManager.resetContentListAndSearchForm();
        agentDataOverviewManager.resetDataOverview();
        agentCompanyAssetManager.resetTemplatesAndSearchForm();
        agentOperationLogManager.resetOperationLogsAndSearchForm();
      }
    });

    return () => {
      removeAfterListener();
    };
  }, [page]);
}

/** 
 * 创建智能体用户
 * @description 该接口已废弃
 */
const useCreateUser = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateUserFormData>(userManagementManager.getCreateFormData());

  const { nameError, genderError, emailError, companyError, personalTitleError, resetError, validateMap, validateAll } = useValidateFormData();

  // 创建用户
  const createUser = async () => {
    setLoading(true);
    // 调接口前的兜底校验
    const valid = validateAll(createFormData);
    if (!valid) {
      setLoading(false);
      return false;
    }
    try {
      await userManagementManager.createUser(createFormData);
      return true
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const changeValue = (key: keyof CreateUserFormData, value: string) => {
    // 输入即校验数据（要保证 key 与 validateMap 中的 key 一致）
    validateMap[key](value);

    userManagementManager.setCreateFormData({
      ...createFormData,
      [key]: value,
    });
  }

  const clearCreateFormData = () => {
    userManagementManager.setCreateFormData({} as CreateUserFormData);
    resetError();
  }

  useEffect(() => {
    return userManagementManager.onCreateFormDataChange((e) => {
      setCreateFormData(e.detail);
    });
  }, []);

  return {
    loading,
    createFormData,
    changeValue,
    createUser,
    clearCreateFormData,
    nameError,
    genderError,
    emailError,
    companyError,
    personalTitleError,
  }
}

// 校验表单数据
const useValidateFormData = () => {
  const { t } = useTranslation();
  const [nameError, setNameError] = useState('');
  const [genderError, setGenderError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [personalTitleError, setPersonalTitleError] = useState('');

  // 校验 username
  const validateName = (value: string) => {
    if (!value || value.length < 2 || value.length > 20) {
      setNameError(t('agent_dashboard.user_management.create_modal.name_placeholder'));
      return false;
    } else {
      setNameError('');
      return true;
    }
  }

  // 校验 gender
  const validateGender = (value: string) => {
    if (!value || !['male', 'female'].includes(value)) {
      setGenderError(t('agent_dashboard.user_management.create_modal.gender_placeholder'));
      return false;
    } else {
      setGenderError('');
      return true;
    }
  }

  // 校验 email
  const validateEmail = (value: string) => {
    // 检查格式：邮箱中必须包含@符号、@符号前后必须有字符、域名部分必须包含至少一个点、不允许包含空白字符
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(value)) {
      setEmailError(t('agent_dashboard.user_management.create_modal.email_address_help_text'));
      return false;;
    } else {
      setEmailError('');
      return true;
    }
  }

  // 校验 company
  const validateCompany = (value: string) => {
    if (!value || value.length < 1 || value.length > 50) {
      setCompanyError(t('agent_dashboard.user_management.create_modal.company_help_text'));
      return false;
    } else {
      setCompanyError('');
      return true;
    }
  }

  // 校验 personalTitle
  const validatePersonalTitle = (value: string) => {
    if (!value || value.length < 1 || value.length > 50) {
      setPersonalTitleError(t('agent_dashboard.user_management.create_modal.person_title_placeholder'));
      return false;
    } else {
      setPersonalTitleError('');
      return true;
    }
  }

  // 重置校验错误
  const resetError = () => {
    setNameError('');
    setGenderError('');
    setEmailError('');
    setCompanyError('');
    setPersonalTitleError('');
  }

  const validateMap = {
    username: validateName,
    gender: validateGender,
    email: validateEmail,
    company: validateCompany,
    personalTitle: validatePersonalTitle,
  }

  // 校验全部表单数据
  const validateAll = (formData: CreateUserFormData) => {
    return [
      validateName(formData.username),
      validateGender(formData.gender),
      validateEmail(formData.email),
      validateCompany(formData.company),
      validatePersonalTitle(formData.personalTitle),
    ].every(Boolean);
  }

  return {
    nameError,
    genderError,
    emailError,
    companyError,
    personalTitleError,

    validateMap,
    validateAll,
    resetError,
  }
}

/** 删除智能体用户 */
const useDeleteUser = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const deleteUser = async (userId: string) => {
    setLoading(true);
    try {
      await userManagementManager.deleteUser(userId);
      await userManagementManager.fetchAgentUsersList();
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    deleteUser,
  }
}

/** 切换智能体用户启用禁用 */
const useToggleUserStatus = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const toggleUserStatus = async (userId: string) => {
    setLoading(true);
    try {
      await userManagementManager.toggleUserStatus(userId);
      await userManagementManager.fetchAgentUsersList();
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  return {
    loading,
    toggleUserStatus,
  }
}

// =========== 用户详情 - 基础信息相关的 hook ==============
// 提供获取和设置激活设置标签页的方法
export const useActiveSettingTab = (isReset: boolean = true) => {
  const [activeTab, setActiveTab] = useState<SettingTab>(userManagementManager.getActiveSettingTab());

  useEffect(() => {
    const unsubscribe = userManagementManager.onActiveSettingTabChange((e) => {
      setActiveTab(e.detail.activeSettingTab)
    })
    return unsubscribe
  }, []);

  // 提供设置激活设置标签页的方法
  const changeActiveTab = (tab: SettingTab) => {
    userManagementManager.setActiveSettingTab(tab);
  };

  useEffect(() => {
    // 组件卸载时，将 activeTab 重置为 video
    return () => {
      if (isReset) {
        userManagementManager.setActiveSettingTab('video');
      }
    }
  }, [isReset])

  return {
    activeTab,
    changeActiveTab
  };
}

// 用户基础信息详情数据（上半部分）
export const useAgentV2UsersDetail = () => {
  const params = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [userBasicInfo, setUserBasicInfo] = useState(userManagementManager.getUserBasicInfo());
  const [userAgentInfo, setUserAgentInfo] = useState(userManagementManager.getUserAgentInfo());
  const [subscribeInfo, setSubscribeInfo] = useState(userManagementManager.getSubscribeInfo());

  const [agentUserActors, setAgentUserActors] = useState<AgentUserActorData[]>(userManagementManager.getAgentUserActors());
  const [agentUserVoices, setAgentUserVoices] = useState<Audio[]>(userManagementManager.getAgentUserVoices());

  // 用户基础信息校验
  const {
    nameError,
    genderError,
    validateField: validateUserBasicInfoField,
    validateAll: validateUserBasicInfoAll,
    resetError: resetUserBasicInfoError
  } = useValidateUserBasicInfoForm();

  // 用户智能体信息校验
  const {
    userAgentNameError,
    personalTitleError,
    validateField: validateUserAgentInfoField,
    validateAll: validateUserAgentInfoAll,
    resetError: resetUserAgentInfoError
  } = useValidateUserAgentInfoForm();

  // 初始化用户基础信息详情数据
  const initialUserDetail = async () => {
    setLoading(true);
    try {
      await Promise.all([
        userManagementManager.fetchAgentV2UsersDetail(params?.userId || ''),
        userManagementManager.fetchAgentUserActors(),
        // 初始化智能体个人简介模板信息
        userManagementManager.fetchAgentIntroductionTemplate(params.id)
      ]);
    } catch (error) {
      // navigate(-1) // 查询用户详情失败，则返回上一页
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const changeBasicInfoValue = (key: keyof UserBasicInfoData, value: string) => {
    validateUserBasicInfoField(key, value);

    userManagementManager.setUserBasicInfo({
      ...userBasicInfo,
      [key]: value,
    });
  }

  const changeAgentInfoValue = (key: keyof UserAgentInfoData, value: string) => {
    validateUserAgentInfoField(key, value);

    userManagementManager.setUserAgentInfo({
      ...userAgentInfo,
      [key]: value,
    });
  }

  // 使用智能体个人简介模板信息
  const setInfoTemplateToIntroduction = () => {
    // 获取智能体个人简介模板
    const template = userManagementManager.getAgentPersonalInfoTemplate();
    if (!template) {
      toast.toast({
        description: t('agent_dashboard.user_detail.agent_info.no_template'),
      })
      return;
    }

    // 获取原智能体个人简介
    const originIntroduction = userAgentInfo.introduction || '';
    // 字符数长度限制 20000
    if (originIntroduction?.length >= 20000) {
      toast.toast({
        description: t('agent_dashboard.user_detail.agent_info.intro_maximum'),
      })
      return;
    }

    // 如果原信息+模板 超过20000，则截取模板的部分内容
    if (originIntroduction?.length + template.length > 20000) {
      const length = 20000 - originIntroduction?.length;
      const truncatedTemplate = template.slice(0, length - 1);
      userManagementManager.setUserAgentInfo({
        ...userAgentInfo,
        introduction: originIntroduction
          ? originIntroduction + '\n' + truncatedTemplate
          : truncatedTemplate,
      });
      return;
    }

    userManagementManager.setUserAgentInfo({
      ...userAgentInfo,
      introduction: originIntroduction
        ? originIntroduction + '\n' + template
        : template,
    });
  }


  const changeSubscribeInfoValue = (key: keyof SubscribeInfoData, value: number) => {
    userManagementManager.setSubscribeInfo({
      ...subscribeInfo,
      [key]: value,
    });
  }

  // 调接口前的兜底校验
  const validateUsers = () => {
    const valid = validateUserBasicInfoAll(userBasicInfo) && validateUserAgentInfoAll(userAgentInfo);
    if (!valid) {
      setUpdateLoading(false);
      return false;
    }
    return true
  }

  // 更新用户
  const updateUser = async () => {
    setUpdateLoading(true);
    try {
      await userManagementManager.updateUser();
      // 更新成功后，刷新用户详情、用户列表
      await Promise.all([
        userManagementManager.fetchAgentV2UsersDetail(params?.userId || ''),
        userManagementManager.fetchAgentUsersList(),
      ]);
      return true
    } catch (error) {
      toast.showToastByCode(error);
      return false
    } finally {
      setUpdateLoading(false);
    }
  }

  // 重置用户详情数据
  const resetUserDetail = () => {
    userManagementManager.setUserBasicInfo(userManagementManager.getOriginUserBasicInfo());
    userManagementManager.setUserAgentInfo(userManagementManager.getOriginUserAgentInfo());
    userManagementManager.setSubscribeInfo(userManagementManager.getOriginSubscribeInfo());
    resetUserBasicInfoError();
    resetUserAgentInfoError();
  }

  /** 清空用户详情数据 */
  const clearUserDetail = () => {
    userManagementManager.setUserBasicInfo({} as UserBasicInfoData);
    userManagementManager.setUserAgentInfo({} as UserAgentInfoData);
    userManagementManager.setSubscribeInfo({} as SubscribeInfoData);
    resetUserBasicInfoError();
    resetUserAgentInfoError();
  }

  useEffect(() => {
    userManagementManager.setAgentId(params?.id || '');
    initialUserDetail();
  }, [params?.userId]);

  useEffect(() => {
    return userManagementManager.onUserBasicInfoChange((e) => {
      setUserBasicInfo(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onUserAgentInfoChange((e) => {
      setUserAgentInfo(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onSubscribeInfoChange((e) => {
      setSubscribeInfo(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onAgentUserActorsChange((e) => {
      setAgentUserActors(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onAgentUserVoicesChange((e) => {
      setAgentUserVoices(e.detail);
    });
  }, []);

  return {
    loading,
    userBasicInfo,
    userAgentInfo,
    subscribeInfo,
    agentUserActors,
    agentUserVoices,

    nameError,
    genderError,
    userAgentNameError,
    personalTitleError,

    changeBasicInfoValue,
    changeAgentInfoValue,
    changeSubscribeInfoValue,
    setInfoTemplateToIntroduction,

    updateUser,
    validateUsers,
    updateLoading,
    resetUserDetail,
    clearUserDetail,
  }
}

// 用户基础详情表单校验
const useValidateUserBasicInfoForm = () => {
  const { t } = useTranslation();
  const [nameError, setNameError] = useState('');
  const [genderError, setGenderError] = useState('');

  // 校验 name — 基础信息姓名表单项已隐藏，以下校验已停用
  // const validateName = (value: string) => {
  //   if (!value || value.length < 2 || value.length > 20) {
  //     setNameError(t('agent_dashboard.user_management.create_modal.name_placeholder'));
  //     return false;
  //   } else {
  //     setNameError('');
  //     return true;
  //   }
  // }

  // 校验 gender
  const validateGender = (value: string) => {
    if (!value || !['male', 'female'].includes(value)) {
      setGenderError(t('agent_dashboard.user_management.create_modal.gender_placeholder'));
      return false;
    } else {
      setGenderError('');
      return true;
    }
  }

  // 重置校验错误
  const resetError = () => {
    // setNameError('');
    setGenderError('');
  }

  const validateMap = {
    // name: validateName,
    gender: validateGender,
  }

  const validateField = (name: keyof UserBasicInfoData, value: string | number | string[]) => {
    if (!validateMap[name]) return;
    return validateMap[name](value);
  }

  // 校验所有数据
  const validateAll = (formData: UserBasicInfoData) => {
    return [
      // validateField('name', formData.name),
      validateField('gender', formData.gender),
    ].every(Boolean);
  }

  return {
    nameError,
    genderError,
    resetError,
    validateField,
    validateAll,
  }
}

// 用户智能体表单校验
const useValidateUserAgentInfoForm = () => {
  const { t } = useTranslation();
  const [userAgentNameError, setUserAgentNameError] = useState('');
  const [personalTitleError, setPersonalTitleError] = useState('');

  // 校验 name
  const validateName = (value: string) => {
    if (!value || value.length < 2 || value.length > 20) {
      setUserAgentNameError(t('agent_dashboard.user_management.create_modal.name_placeholder'));
      return false;
    } else {
      setUserAgentNameError('');
      return true;
    }
  }

  // 校验 personalTitle
  const validatePersonalTitle = (value: string) => {
    if (!value || value.length < 1 || value.length > 50) {
      setPersonalTitleError(t('agent_dashboard.user_management.create_modal.person_title_placeholder'));
      return false;
    } else {
      setPersonalTitleError('');
      return true;
    }
  }

  // 重置校验错误
  const resetError = () => {
    setUserAgentNameError('');
    setPersonalTitleError('');
  }

  const validateMap = {
    name: validateName,
    personalTitle: validatePersonalTitle,
  }

  const validateField = (name: keyof UserAgentInfoData, value: string) => {
    if (!validateMap[name]) return;
    return validateMap[name](value);
  }

  // 校验所有数据
  const validateAll = (formData: UserAgentInfoData) => {
    return [
      validateField('name', formData.name),
      validateField('personalTitle', formData.personalTitle),
    ].every(Boolean);
  }

  return {
    userAgentNameError,
    personalTitleError,
    resetError,
    validateField,
    validateAll,
  }
}

// 深度对比用户详情数据是否有变化
const useUserDetailChanged = () => {
  // 对比用户基础信息，用户agent信息，用户订阅信息是否有变化
  const isUserInfoChanged = !deepEqual<UserBasicInfoData>(
    userManagementManager.getUserBasicInfo(),
    userManagementManager.getOriginUserBasicInfo()
  );
  const isUserAgentInfoChanged = !deepEqual<UserAgentInfoData>(
    userManagementManager.getUserAgentInfo(),
    userManagementManager.getOriginUserAgentInfo()
  );
  const isSubscribeInfoChanged = !deepEqual<SubscribeInfoData>(
    userManagementManager.getSubscribeInfo(),
    userManagementManager.getOriginSubscribeInfo()
  );

  // 对比文章设置是否有变化
  const isArticleSettingChanged = !deepEqual<ArticleSettingData>(
    userManagementManager.getArticleSetting(),
    userManagementManager.getOriginArticleSetting()
  );

  // 对比用户推送设置是否有变化
  const isPushSettingsChanged = !deepEqual<PushSettingData[]>(
    userManagementManager.getPushSettings(),
    userManagementManager.getOriginPushSettings()
  );

  return {
    isUserInfoChanged: isUserInfoChanged || isUserAgentInfoChanged || isSubscribeInfoChanged,
    isPushSettingsChanged: isPushSettingsChanged,
    isArticleSettingChanged,
  }
}

// 上传智能体用户头像
const useUploadUserAvatar = () => {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    try {
      setUploading(true);
      await userManagementManager.uploadUserAvatar(file);
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    } finally {
      setUploading(false);
    }
  };

  return {
    upload,
    uploading,
  }
}

// =========== 用户详情 - 资产数据（数字人、音频）相关的hook ==============
// 演员重命名
export const useActorRename = () => {
  const toast = useToast();
  const params = useParams();

  const rename = async ({ id, name }: { id: string, name: string }) => {
    try {
      const res = await userManagementManager.actorRename({ id, name, userId: params?.userId });
      // 重名检查
      if (res.isDuplicate) {
        toast.toast({ description: 'Actor name already exists' });
      }
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    rename,
  }
}

// 删除演员
export const useDeleteActor = () => {
  const toast = useToast();
  const params = useParams();

  const deleteActor = async (id: string) => {
    try {
      await userManagementManager.actorDelete({ id });
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to delete actor:", error);
      throw error
    }
  }

  return {
    deleteActor
  }
}

// 删除音频
export const useDeleteVoice = () => {
  const toast = useToast();
  const params = useParams();
  const { t } = useTranslation();

  const deleteVoice = async (id: string) => {
    try {
      const res = await userManagementManager.voiceDelete({ id, userId: params?.userId || '' });
      if (res.isUsed) {
        toast.toast({ description: t('agent_dashboard.user_detail.voice_card.delete_fail') });
      }
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to delete voice:", error);
      throw error
    }
  }

  return {
    deleteVoice
  }
}

// 资产音频重命名
export const useVoiceRename = () => {
  const { error, showToastByCode } = useToast();
  const userId = new URLSearchParams(window.location.search).get('userId');

  const renameVoice = async ({ voiceId, name }: { voiceId: string, name: string }) => {
    try {
      const res = await userManagementManager.voiceRename({ voiceId, name, userId: userId || '' });
      // 重名检查
      if (res.isDuplicate) {
        error({ description: 'Voice name already exists' });
      } else {
        // 重命名成功后刷新播客音频数据
        await userManagementManager.fetchPodcastVoices({ userId });
      }
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    renameVoice,
  };
};

// 搜索资产音频
export const useSearchVoice = () => {
  const toast = useToast();
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);

  const searchVoice = async (text = null) => {
    try {
      setLoading(true);
      await userManagementManager.fetchAgentUserActors(text ?? inputText);
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  }

  const changeValue = (text: string) => {
    setInputText(text);
  }

  return {
    inputText,
    loading,
    searchVoice,
    changeValue,
  }
}

// 停止轮询的钩子
export const useVideoCreationPollingCleanup = () => {
  useEffect(() => {
    // 返回清理函数，在组件卸载时执行
    return () => {
      // 停止轮询
      userManagementManager.stopAllPolling();
    };
  }, []);
};

// 重新生成数字人
export const useRegenerateActor = () => {
  const toast = useToast();
  const params = useParams();
  // 特殊处理：从 URL 查询参数中获取 userId（传递给->[获取用户数字人/音频数据]接口）
  const _userId = new URLSearchParams(location.search).get('userId');

  const regenerateActor = async (actor: Actor) => {
    try {
      await userManagementManager.regenerateActor(actor, _userId || '');
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to regenerate actor:", error);
      throw error
    }
  }

  return {
    regenerateActor,
  }
}

// 选中的数字人表单数据
export const useSelectedActor = () => {
  const [selectedActorFormData, setSelectedActorFormData] = useState<SelectedActorFormData>(userManagementManager.getSelectedActorFormData());

  const selectedActor = (actor: Actor) => {
    userManagementManager.setSelectedActorFormData({
      actorName: actor.name,
      actorId: actor.id,
      voiceName: actor.audio.name,
      voiceId: actor.audio.id,
      voiceUrl: actor.audio.audioUrl,
    });
  }

  useEffect(() => {
    return userManagementManager.onSelectedActorFormDataChange((e) => {
      setSelectedActorFormData(e.detail);
    });
  }, []);

  return {
    selectedActorFormData,
    selectedActor,
  }
}

// 保存编辑数字人
export const useSaveSelectedActor = () => {
  const toast = useToast();
  const params = useParams();
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");
  const selectedActorFormData = userManagementManager.getSelectedActorFormData();
  const [loading, setLoading] = useState(false);

  const [isAllowSave, setIsAllowSave] = useState(true);
  const [duplicateNameMsg, setDuplicateNameMsg] = useState("");
  const setDebounceRef = useRef<number | null>(null); // 防抖引用

  const { actorNameError, voiceNameError, voiceIdError, validateMap, validateAll, resetError } = useValidateSelectedActorFormData();

  // 在用户管理详情页，使用 userId 作为参数
  const userId = (isAgentUserManagementDetail && params.userId) ? params.userId : undefined;

  const changeValue = (key: keyof SelectedActorFormData, value: string) => {
    // 输入即校验数据（要保证 key 与 validateMap 中的 key 一致）
    validateMap[key](value);

    // 清除之前的防抖定时器
    if (setDebounceRef.current) {
      clearTimeout(setDebounceRef.current);
    }

    userManagementManager.setSelectedActorFormData({
      ...selectedActorFormData,
      [key]: value,
    });

    // 重名校验
    if (key === 'actorName' && value) {
      setIsAllowSave(false);
      setDuplicateNameMsg("");
      checkName()
    };
    if (key === 'actorName' && !value) {
      setDuplicateNameMsg("");
    }
  }

  // 保存编辑数字人
  const save = async () => {
    setLoading(true);
    // 调接口前的兜底校验
    const valid = validateAll(selectedActorFormData);
    if (!valid) {
      setLoading(false);
      return false;
    }
    try {
      await userManagementManager.saveSelectedActor();
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  // 检查演员名称是否重复
  const checkName = async () => {
    setDebounceRef.current = window.setTimeout(async () => {
      setIsAllowSave(await checkNameDuplicate(userId));
    }, 150);
  };

  const checkNameDuplicate = async (userId?: string) => {
    try {
      const res = await userManagementManager.checkActorNameDuplicate(userId);
      if (res.isDuplicate) {
        setDuplicateNameMsg("Actor name already exists");
        return false
      }
      return true;
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    }
  }

  return {
    loading,
    save,
    isAllowSave,
    duplicateNameMsg,
    actorNameError,
    voiceNameError,
    voiceIdError,
    changeValue,
    resetError,
  }
}

// 深度对比选中的数字人表单数据是否有变化
export const useSelectedActorFormDataChanged = () => {
  const record = userManagementManager.getAgentUserActors().find((item) => {
    return item.id === userManagementManager.getSelectedActorFormData().actorId;
  });

  const buildSelectedActorFormData = (actor: Actor) => ({
    actorName: actor.name,
    actorId: actor.id,
    voiceName: actor.audio.name,
    voiceId: actor.audio.id,
    voiceUrl: actor.audio.audioUrl,
  })

  const reset = () => {
    userManagementManager.setSelectedActorFormData(buildSelectedActorFormData(record));
  }

  return {
    isChanged: !deepEqual<SelectedActorFormData>(
      userManagementManager.getSelectedActorFormData(),
      record ? buildSelectedActorFormData(record) : {} as SelectedActorFormData
    ),
    reset
  }
}

// 校验选中的数字人表单数据
export const useValidateSelectedActorFormData = () => {
  const { t } = useTranslation();
  const [actorNameError, setActorNameError] = useState('');
  const [voiceNameError, setVoiceNameError] = useState('');
  const [voiceIdError, setVoiceIdError] = useState('');

  // 校验 actorName 数据
  const validateActorName = (value: string) => {
    if (!value) {
      setActorNameError(t('Please input actor name'));
      return false;
    } else {
      setActorNameError('');
      return true;
    }
  }

  // 校验 voiceName 数据
  const validateVoiceName = (value: string) => {
    if (!value) {
      setVoiceNameError(t('Please input voice name'));
      return false;
    } else {
      setVoiceNameError('');
      return true;
    }
  }

  // 校验 voiceId 数据
  const validateVoiceId = (value: string) => {
    if (!value) {
      setVoiceNameError(t('Please input voice id'));
      return false;
    } else {
      setVoiceNameError('');
      return true;
    }
  }

  // 校验所有数据
  const validateAll = (formData: SelectedActorFormData) => {
    return [
      validateActorName(formData.actorName),
      validateVoiceName(formData.voiceName),
    ].every(Boolean);
  }

  // 重置校验错误
  const resetError = () => {
    setActorNameError('');
    setVoiceNameError('');
    setVoiceIdError('');
  }

  const validateMap = {
    actorName: validateActorName,
    voiceName: validateVoiceName,
    voiceId: validateVoiceId,
  }

  return {
    actorNameError,
    voiceNameError,
    voiceIdError,

    validateMap,
    validateAll,
    resetError,
  }
}

// 当前用户的资产音频数据
export const useUserAssetsVoices = () => {
  const params = useParams();
  const userId = params.userId;

  const [voices, setVoices] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await assetsVoiceManager.fetchUserVoices(userId);
      setVoices(res || []);
    } catch (error) {
      console.error("fetch user assets voices failed", error);
      setVoices([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [userId])

  return {
    voices,
    loading,
  }
}

// 操作数字人数据
export const useActorAction = () => {
  const addActor = (actor: AgentUserActorData | AgentUserActorData[]) => {
    if (Array.isArray(actor)) {
      userManagementManager.setAgentUserActors(actor);
    } else {
      // 过滤掉替换的数字人
      const newActors = userManagementManager.getAgentUserActors().filter(item => item.id !== actor.id)
      userManagementManager.setAgentUserActors([actor, ...newActors]);
    }
  }

  return {
    addActor,
  }
}

// ==================== 推送设置 =======================
// 推送设置的数据
export const usePushSettings = () => {
  const toast = useToast();
  const { id } = useParams();
  const { t } = useTranslation();
  const _userId = new URLSearchParams(window.location.search).get("userId");

  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [pushSettings, setPushSettings] = useState(userManagementManager.getPushSettings());
  const [subscriptionVisibility, setSubscriptionVisibility] = useState(userManagementManager.getSubscriptionVisibility());
  const [pushSettingVisibility, setPushSettingVisibility] = useState(userManagementManager.getPushSettingVisibility());

  const { getCurrentErrors, validateField, validateAll, resetError } = useValidatePushSettings();

  // 请求推送设置数据
  const fetchPushSettings = async () => {
    setLoading(true);
    if (!id || !_userId) return;
    userManagementManager.setAgentId(id);
    try {
      await userManagementManager.fetchPushSettings({ agentId: id, userId: _userId });
    } catch (error) {
      console.error("fetch push settings failed", error);
      toast.showToastByCode(error);
      setPushSettings([]);
    } finally {
      setLoading(false);
    }
  }

  const validatePushSettings = () => {
    // 校验所有数据
    if (!validateAll(pushSettings, pushSettingVisibility)) {
      return false;
    }
    return true;
  }

  // 更新推送设置数据
  const updatePushSettings = async () => {
    setUpdateLoading(true);
    try {
      // 用外面列表的user_id
      await userManagementManager.updatePushSettings(_userId);
      // 更新成功后，刷新推送设置
      await Promise.all([
        userManagementManager.fetchPushSettings({ agentId: id, userId: _userId }),
      ]);
      resetError();
      return true;
    } catch (error) {
      console.error("update push settings failed", error);
      toast.showToastByCode(error);
      return false;
    } finally {
      setUpdateLoading(false);
    }
  }

  // 重置推送设置数据
  const resetPushSettings = () => {
    // 回到默认值
    const defaultSettings = userManagementManager.getOriginPushSettings().map(item => {
      return {
        type: item.type,
        // 设置默认值
        pushFrequencyDay: item.pushFrequencyDay ?? 1,
        pushCount: item.pushCount ?? 3,
        pushTime: item.pushTime ?? 8,
      }
    })
    userManagementManager.setPushSettings(defaultSettings);
    resetError();
  }

  // 切换推送设置类型时，请求最新数据
  useEffect(() => {
    fetchPushSettings();
  }, []);

  // 实时获取当前选中的推送设置
  const currentPushSetting = useMemo(() => {
    return pushSettings.find(item => item.type === PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()]);
  }, [pushSettings, userManagementManager.getActiveSettingTab()]);

  // 获取当前推送设置的错误信息
  const currentErrors = useMemo(() => {
    if (!currentPushSetting) return { frequencyError: '', countError: '', timeError: '' };
    return getCurrentErrors(currentPushSetting.type);
  }, [currentPushSetting, getCurrentErrors]);

  const changePushSettings = (key: keyof PushSettingsData, value: number) => {
    if (!currentPushSetting) return;

    // 输入即校验数据
    validateField(key, value, currentPushSetting.type);

    // 更新推送设置数据
    const updatedSettings = pushSettings.map(item => {
      // 只更新当前选中的推送设置
      if (item.type === PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()]) {
        return { ...item, [key]: value };
      }
      return item;
    });
    userManagementManager.setPushSettings(updatedSettings);
  }

  // 当切换推送设置类型时，可以选择是否重置错误
  useEffect(() => {
    // 这里可以选择在切换标签时是否重置当前标签的错误
    // resetError(currentPushSetting?.type);
  }, [currentPushSetting?.type, resetError]);

  useEffect(() => {
    return userManagementManager.onPushSettingsChange((e) => {
      setPushSettings(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onSubscriptionVisibilityChange((e) => {
      setSubscriptionVisibility(e.detail);
    });
  }, []);

  useEffect(() => {
    return userManagementManager.onPushSettingVisibilityChange((e) => {
      setPushSettingVisibility(e.detail);
    });
  }, []);

  return {
    loading,
    updateLoading,
    pushSettings,
    subscriptionVisibility,
    pushSettingVisibility,
    currentPushSetting,
    frequencyError: currentErrors.frequencyError,
    countError: currentErrors.countError,
    timeError: currentErrors.timeError,
    changePushSettings,
    resetPushSettings,
    updatePushSettings,
    validatePushSettings,
  }
}

// 校验推送设置数据
const useValidatePushSettings = () => {
  const { t } = useTranslation();
  const toast = useToast();
  // 使用对象存储不同类型推送设置的错误信息
  const [errors, setErrors] = useState<Record<number, { frequencyError: string; countError: string; timeError: string }>>({});

  const typeMap = {
    '1': t('agent_dashboard.content_management.table.type_map.video'),
    '2': t('agent_dashboard.content_management.table.type_map.podcast'),
    '3': t('agent_dashboard.content_management.table.type_map.moments'),
    '4': t('agent_dashboard.content_management.table.type_map.image'),
    '5': t('agent_dashboard.content_management.table.type_map.article'),
  }

  // 获取当前推送类型的错误信息
  const getCurrentErrors = (pushType: number) => {
    return errors[pushType] || { frequencyError: '', countError: '', timeError: '' };
  };

  // 设置特定推送类型的错误信息
  const setTypeErrors = (pushType: number, newErrors: { frequencyError?: string; countError?: string; timeError?: string }) => {
    setErrors(prev => ({
      ...prev,
      [pushType]: {
        ...prev[pushType],
        ...newErrors,
      },
    }));
  };

  // 校验 frequency 数据
  const validateFrequency = (value: number, pushType: number) => {
    if (value <= 0) {
      setTypeErrors(pushType, { frequencyError: t('agent_dashboard.user_detail.push_settings.push_frequency_valid') });
      return false;
    } else {
      setTypeErrors(pushType, { frequencyError: '' });
      return true;
    }
  }

  // 校验 count 数据
  const validateCount = (value: number, pushType: number) => {
    if (value <= 0) {
      setTypeErrors(pushType, { countError: t('agent_dashboard.user_detail.push_settings.push_amount_valid') });
      return false;
    } else if (value > 3) {
      setTypeErrors(pushType, { countError: t('agent_dashboard.user_detail.push_settings.push_amount_valid_max') });
      return false;
    } else {
      setTypeErrors(pushType, { countError: '' });
      return true;
    }
  }

  // 校验 pushTime 数据
  const validatePushTime = (value: number, pushType: number) => {
    if (!value) {
      setTypeErrors(pushType, { timeError: t('agent_dashboard.user_detail.push_settings.push_time_placeholder') });
      return false;
    } else {
      setTypeErrors(pushType, { timeError: '' });
      return true;
    }
  }

  // 校验所有数据
  const validateAll = (pushSettings: PushSettingData[], pushSettingVisibility: Record<number, number>) => {
    // 1、只校验对应订阅设置为1的推送表单数据
    // 2、并且 pushSettingVisibility 为 对应的类型为1
    const filteredPushSettings = pushSettings.filter(pushSetting => {
      // 取出推送设置的type对应的键名
      let subscribeKey: SubscriptionType | undefined;
      for (const [key, value] of Object.entries(PUSH_SETTINGS_TYPE)) {
        if (value === pushSetting.type) {
          subscribeKey = key as SubscriptionType;
          break;
        }
      }
      // 根据订阅设置键名获取订阅设置值
      if (subscribeKey) {
        const subscribeSetting = userManagementManager.getSubscribeInfo()[SUBSCRIPTION_MAP[subscribeKey]];
        // 如果订阅设置为1（开启）
        // 并且 pushSettingVisibility 为 对应类型的值为1
        // 则保留该项进行校验
        return subscribeSetting === 1 && pushSettingVisibility[PUSH_SETTINGS_TYPE[subscribeKey]] === 1;
      }
      return true;
    });

    // 校验过滤后的推送设置数据，并在遇到第一个不通过的项时激活对应tab
    for (const item of filteredPushSettings) {
      const pushType = item.type;

      // 检查每一项校验
      const frequencyValid = validateFrequency(item.pushFrequencyDay, pushType);
      const countValid = validateCount(item.pushCount, pushType);
      const timeValid = validatePushTime(item.pushTime, pushType);

      if (!frequencyValid || !countValid || !timeValid) {
        // 通过pushType值找到对应的键名
        for (const [key, value] of Object.entries(PUSH_SETTINGS_TYPE)) {
          if (value === pushType) {
            // 设置激活的tab
            userManagementManager.setActiveSettingTab(key as SettingTab);
            toast.toast({
              description: t('agent_dashboard.user_detail.push_settings.update_valid', { type: typeMap[value] }),
            });
            break;
          }
        }
        return false;
      }
    }

    return true;
  }

  // 重置校验错误
  const resetError = (pushType?: number) => {
    if (pushType !== undefined) {
      // 重置特定类型的错误
      setTypeErrors(pushType, { frequencyError: '', countError: '', timeError: '' });
    } else {
      // 重置所有错误
      setErrors({});
    }
  }

  const validateMap = {
    pushFrequencyDay: validateFrequency,
    pushCount: validateCount,
    pushTime: validatePushTime,
  }

  const validateField = (name: keyof PushSettingsData, value: string | number | string[], pushType: number) => {
    if (!validateMap[name]) return;
    return validateMap[name](Number(value), pushType);
  }

  return {
    getCurrentErrors,
    validateField,
    validateAll,
    resetError,
  }
}

// 播客音频数据
export const usePodcastVoices = () => {
  const params = useParams();
  const toast = useToast();
  // 从URL参数中获取userId
  const userId = new URLSearchParams(window.location.search).get('userId');

  const [podcastVoices, setPodcastVoices] = useState(userManagementManager.getPodcastVoices());
  const [loading, setLoading] = useState(false);

  const fetchPodcastVoices = async () => {
    setLoading(true);
    try {
      await userManagementManager.fetchPodcastVoices({ agentId: params.id, userId });
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to fetch agent user actors:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodcastVoices();
  }, []);

  useEffect(() => {
    return userManagementManager.onPodcastVoicesChange((e) => {
      setPodcastVoices(e.detail);
    });
  }, []);

  return {
    loading,
    podcastVoices,
  }
}

// 保存播客音频
export const usePodcastVoicesSave = () => {
  const toast = useToast();
  const params = useParams();
  const { t } = useTranslation();

  // 从URL参数中获取userId
  const userId = new URLSearchParams(window.location.search).get('userId');
  const [loading, setLoading] = useState(false);

  const saveVoices = async (ids: string[]) => {
    if (!userId || !params.id) {
      toast.toast({ description: 'userId or agentId is required' });
      return;
    }
    try {
      setLoading(true);

      // 根据ids，过滤掉 podcastVoices 中存在的项
      const validIds = ids.filter(id => !userManagementManager.getPodcastVoices().some(voice => voice.id === id));

      if (validIds.length === 0) {
        toast.toast({
          description: t('agent_dashboard.user_detail.voice.add_modal.save_valid_tips')
        });
        return;
      }

      // 构建参数
      const templates = validIds.map(id => {
        return {
          userId,
          agentId: params.id,
          userAudioTemplateId: id,
        }
      })
      await userManagementManager.savePodcastVoices(templates);

      // 保存成功后刷新播客音频数据
      await userManagementManager.fetchPodcastVoices({ userId });
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to save podcast voices:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    saveVoices,
  }
}

// 删除播客音频
export const usePodcastVoicesDelete = () => {
  const toast = useToast();
  // 从URL参数中获取userId
  const userId = new URLSearchParams(window.location.search).get('userId');

  const deleteVoice = async (id: string) => {
    if (!userId) {
      toast.toast({ description: 'userId is required' });
      return;
    }
    try {
      await userManagementManager.deletePodcastVoice({
        id,
        userId: userId || ''
      });

    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to delete voice:", error);
      throw error
    }
  }

  return {
    deleteVoice
  }
}

// 播客音频重命名
export const usePodcastVoicesRename = () => {
  const { error, showToastByCode } = useToast();
  const userId = new URLSearchParams(window.location.search).get('userId');

  const renameVoice = async ({ voiceId, name }: { voiceId: string, name: string }) => {
    if (!userId) {
      error({ description: 'userId is required' });
      return;
    }
    try {
      const res = await userManagementManager.renamePodcastVoice({ voiceId, name, userId });
      // 重名检查
      if (res.isDuplicate) {
        error({ description: 'Voice name already exists' });
      } else {
        // 重命名成功后刷新用户资产音频数据
        await userManagementManager.fetchAgentUserActors();
      }
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    renameVoice,
  };
};

// 文章设置数据（页眉，引流底图）
export const useArticleSettings = () => {
  const toast = useToast();
  const params = useParams();
  const _userId = new URLSearchParams(window.location.search).get('userId');

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [articleSetting, setArticleSetting] = useState(userManagementManager.getArticleSetting());

  const changeValue = (key: keyof ArticleSettingData, value: string) => {
    userManagementManager.setArticleSetting({
      ...articleSetting,
      [key]: value,
    });
  }

  // 更新文章设置
  const updateArticleSetting = async () => {
    setUpdating(true);
    try {
      await userManagementManager.saveArticleSetting({
        agentId: params.id,
        userId: _userId,
      });
      return true
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to save article setting:", error);
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  // 上传引流底图
  const uploadArticleSettingImage = async (file: File) => {
    try {
      setUploading(true);
      await userManagementManager.uploadArticleSettingImage(file);
    } catch (error) {
      toast.showToastByCode(error);
      console.error("Failed to upload article setting image:", error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // 监听文章设置变化
  useEffect(() => {
    return userManagementManager.onArticleSettingChange((e) => {
      setArticleSetting(e.detail);
    });
  }, []);

  return {
    loading,
    uploading,
    updating,
    articleSetting,

    changeValue,
    updateArticleSetting,
    uploadArticleSettingImage,
  }
}

export const useUserManagementHooks = {
  // 用户列表操作
  useAgentUserListData,
  useCreateUser,
  useDeleteUser,
  useToggleUserStatus,

  // 活动设置标签
  useActiveSettingTab,

  // 用户详情
  useAgentV2UsersDetail,
  useUserDetailChanged,
  useUploadUserAvatar,
  useValidateFormData,

  // 演员/音频操作
  useActorAction,
  useActorRename,
  useVoiceRename,
  useRegenerateActor,
  useSelectedActor,
  useSaveSelectedActor,
  useSelectedActorFormDataChanged,
  useUserAssetsVoices,
  useListenerAfterAction,
  useDeleteActor,
  useDeleteVoice,
  useSearchVoice,

  // 推送设置
  usePushSettings,

  // 播客音频数据操作
  usePodcastVoices,
  usePodcastVoicesSave,
  usePodcastVoicesRename,
  usePodcastVoicesDelete,

  // 文章设置
  useArticleSettings,
}

// 导出单例实例
export const userManagementManager = AgentUserManagementManager.getInstance();
